class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = null;
    this.requestSequence = 0;
    this.pendingSearch = null;
    this.searchTerm = '';
    this.predictiveSearchSupported = this.supportsPredictiveSearch();

    if (!this.input || !this.predictiveSearchResults || !this.predictiveSearchSupported) {
      this.disablePredictiveSearch();
      return;
    }

    this.setupEventListeners();
  }

  supportsPredictiveSearch() {
    const locale = document.documentElement.lang.toLowerCase().split('-')[0];
    try {
      const features = JSON.parse(document.getElementById('shopify-features')?.textContent || '{}');
      if (features.predictiveSearch === false) return false;
      const featureLocale = typeof features.locale === 'string' ? features.locale.toLowerCase().split('-')[0] : '';
      if (features.predictiveSearch === true && (!featureLocale || featureLocale === locale)) return true;
    } catch {}
    return locale !== 'mt';
  }

  disablePredictiveSearch() {
    this.cancelPendingSearch();
    this.predictiveSearchSupported = false;
    this.isOpen = false;
    ['open', 'loading', 'results'].forEach((attribute) => this.removeAttribute(attribute));
    if (this.predictiveSearchResults) this.predictiveSearchResults.hidden = true;
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (this.statusElement) {
      this.statusElement.textContent = '';
      this.statusElement.setAttribute('aria-hidden', 'true');
    }
    if (!this.input) return;
    if (this.input.getAttribute('role') === 'combobox') this.input.removeAttribute('role');
    ['aria-expanded', 'aria-owns', 'aria-controls', 'aria-haspopup', 'aria-autocomplete', 'aria-activedescendant'].forEach(
      (attribute) => this.input.removeAttribute(attribute)
    );
  }

  settleSearch(request, result, error) {
    if (!request || request.settled) return;
    request.settled = true;
    if (error) request.deferred?.reject(error);
    else request.deferred?.resolve(result);
    if (this.pendingSearch === request) this.pendingSearch = null;
  }

  cancelPendingSearch() {
    this.requestSequence += 1;
    this.abortController?.abort();
    this.abortController = null;
    this.settleSearch(this.pendingSearch, null, new DOMException('Search cancelled', 'AbortError'));
  }

  isCurrentSearch(request) {
    return this.predictiveSearchSupported && request.sequence === this.requestSequence &&
      request.term === this.getQuery() && this.contains(document.activeElement);
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', () => {
      if (this.predictiveSearchSupported && this.getQuery() !== this.searchTerm) {
        this.close();
        this.removeAttribute('results');
      }
    });
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    if (!this.predictiveSearchSupported) return;
    if (!this.contains(document.activeElement)) {
      this.close();
      return;
    }
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.predictiveSearchSupported) return;
    if (!this.getQuery().length || (this.isOpen && this.getQuery() === this.searchTerm &&
      this.querySelector('[aria-selected="true"] a'))) event.preventDefault();
    else this.close();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (!this.predictiveSearchSupported) return;
    if (super.shouldResetForm()) {
      this.close(true);
    }
  }

  onFocus() {
    if (!this.predictiveSearchSupported) return;
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    if (!this.predictiveSearchSupported) return;
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.predictiveSearchSupported) return;
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (!this.predictiveSearchSupported) return;
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (!previousTerm || !currentButtonText) return;
    const parts = currentButtonText.split(previousTerm);
    if (parts.length === 2) searchForTextElement.innerText = parts[0] + newTerm + parts[1];
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    if (!allVisibleElements.length) return;
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    if (!this.isOpen || this.getQuery() !== this.searchTerm) return;
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  async getSearchResults(searchTerm) {
    this.cancelPendingSearch();
    if (!this.predictiveSearchSupported || !searchTerm || searchTerm !== this.getQuery()) return;
    const request = {
      sequence: this.requestSequence,
      term: searchTerm,
      deferred: this.dispatchSearchUpdateEvent(searchTerm),
      settled: false,
    };
    this.pendingSearch = request;
    this.closeResults();
    this.removeAttribute('results');
    this.setLiveRegionLoadingState();

    try {
      if (Object.prototype.hasOwnProperty.call(this.cachedResults, searchTerm)) {
        if (this.isCurrentSearch(request)) {
          this.renderSearchResults(this.cachedResults[searchTerm]);
          this.settleSearch(request, { totalCount: this.getTotalResultCount() });
        } else {
          this.settleSearch(request, null, new DOMException('Search cancelled', 'AbortError'));
        }
        return;
      }

      const controller = new AbortController();
      this.abortController = controller;
      const response = await fetch(
        `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
        { signal: controller.signal }
      );
      if (!response.ok) {
        const error = new Error(`Predictive search unavailable (${response.status})`);
        error.status = response.status;
        throw error;
      }

      const text = await response.text();
      if (!this.isCurrentSearch(request)) {
        this.settleSearch(request, null, new DOMException('Search cancelled', 'AbortError'));
        return;
      }
      const results = new DOMParser().parseFromString(text, 'text/html')
        .querySelector('#shopify-section-predictive-search');
      if (!results?.querySelector('[data-predictive-search-live-region-count-value]')) {
        throw new Error('Predictive search returned an invalid response');
      }
      const resultsMarkup = results.innerHTML;
      this.allPredictiveSearchInstances.forEach((instance) => {
        instance.cachedResults[searchTerm] = resultsMarkup;
      });
      this.renderSearchResults(resultsMarkup);
      this.settleSearch(request, { totalCount: this.getTotalResultCount() });
    } catch (error) {
      const isCurrent = this.isCurrentSearch(request);
      this.settleSearch(request, null, error);
      if (!isCurrent) return;
      if (error.status === 417) this.disablePredictiveSearch();
      else this.close();
    }
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.cancelPendingSearch();
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    this.isOpen = false;
    if (clearSearchTerm) {
      this.searchTerm = '';
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
